import { normalizeGenerationReferences } from './generation-reference';
import {
  buildGenerationReferenceIdentity,
  parseGenerationReferenceIdentity,
  type LocalGenerationReferenceToken,
} from './generation-reference-draft-reconcile';
import { rememberReferenceSourceHint } from './reference-source-hints';
import { buildMentionDisplay } from '@/utils/mention';

import type {
  FrameImage,
  MentionToken,
  ReferenceImage,
  VideoGenerationMode,
} from '@/types/conversation-input';
import type {
  GenerationReferenceItem,
  GenerationReferenceRoleValue,
  GenerationResourceTypeValue,
} from '@/types/generation-reference';
import { GenerationReferenceRole, GenerationResourceType } from '@/types/generation-reference';
import type { MentionResource, MentionResourceType } from '@/types/mention';

function roleFromMedia(
  mimeType: string | undefined,
  resourceType: GenerationResourceTypeValue | undefined,
  mentionType?: MentionResourceType
): GenerationReferenceRoleValue {
  if (
    mentionType === 'video' ||
    mimeType?.startsWith('video/') ||
    resourceType === GenerationResourceType.STORYBOARD_VIDEO
  ) {
    return GenerationReferenceRole.REFERENCE_VIDEO;
  }
  if (
    mentionType === 'sound' ||
    mentionType === 'voice' ||
    mimeType?.startsWith('audio/') ||
    resourceType === GenerationResourceType.SOUND_EFFECT ||
    resourceType === GenerationResourceType.VOICE_OVER
  ) {
    return GenerationReferenceRole.REFERENCE_AUDIO;
  }
  return GenerationReferenceRole.REFERENCE_IMAGE;
}

function normalizeMentionSnapshotMime(resource: MentionResource): string | undefined {
  const raw = resource.mimeType;
  if (resource.type === 'video' && raw && !raw.toLowerCase().startsWith('video/')) {
    return 'video/mp4';
  }
  if (
    (resource.type === 'sound' || resource.type === 'voice') &&
    raw &&
    !raw.toLowerCase().startsWith('audio/')
  ) {
    return 'audio/mpeg';
  }
  return raw;
}

function resourceTypeFromReference(
  reference: ReferenceImage
): GenerationResourceTypeValue | undefined {
  return reference.resourceType;
}

function buildReferenceFromFrame(
  frame: FrameImage | null | undefined,
  role: GenerationReferenceRoleValue
): GenerationReferenceItem | null {
  if (!frame?.resourceId || !frame.resourceType) return null;
  const url = frame.remoteUrl ?? frame.url;
  if (!url) return null;
  return {
    resourceType: frame.resourceType,
    resourceId: frame.resourceId,
    role,
    ordinal: 0,
    snapshot: {
      name:
        role === GenerationReferenceRole.FIRST_FRAME
          ? '首帧'
          : role === GenerationReferenceRole.LAST_FRAME
            ? '尾帧'
            : '续写源视频',
      url,
      thumbnailUrl: frame.url || undefined,
      mimeType: frame.mimeType,
      durationSeconds: frame.durationSeconds,
    },
  };
}

function buildReferenceFromToken(
  token: MentionToken,
  referenceImages: ReferenceImage[]
): GenerationReferenceItem | null {
  if (token.kind === 'resource' && token.resource) {
    const resource = token.resource;
    const resourceId = resource.submitResourceId;
    const resourceType = resource.submitResourceType;
    if (!resourceId || !resourceType || !resource.resourceUrl) return null;
    rememberReferenceSourceHint(resource);
    return {
      resourceType,
      resourceId,
      role:
        resource.generationReferenceRole ??
        roleFromMedia(resource.mimeType, resourceType, resource.type),
      ordinal: 0,
      snapshot: {
        name: token.display.replace(/^@/, '').trim() || resource.elementName,
        url: resource.resourceUrl,
        thumbnailUrl: resource.thumbnailUrl,
        mimeType: normalizeMentionSnapshotMime(resource),
        width: resource.width,
        height: resource.height,
        durationSeconds: resource.durationSeconds,
      },
    };
  }
  if (token.kind !== 'reference-image' || !token.referenceImageId) return null;
  const reference = referenceImages.find((item) => item.id === token.referenceImageId);
  const resourceType = reference ? resourceTypeFromReference(reference) : undefined;
  const resourceId = reference?.resourceId;
  const url = reference?.remoteUrl ?? token.referenceImageUrl;
  if (!resourceType || !resourceId || !url) return null;
  return {
    resourceType,
    resourceId,
    role: roleFromMedia(reference?.mimeType ?? token.referenceImageMimeType, resourceType),
    ordinal: 0,
    snapshot: {
      name: token.display.replace(/^@/, '').trim(),
      url,
      thumbnailUrl: reference?.url,
      mimeType: reference?.mimeType ?? token.referenceImageMimeType,
      width: reference?.width ?? undefined,
      height: reference?.height ?? undefined,
      durationSeconds: reference?.durationSeconds,
    },
  };
}

export function buildGenerationReferenceFromMentionResource(
  resource: MentionResource,
  ordinal = 0
): GenerationReferenceItem | null {
  const resourceId = resource.submitResourceId;
  const resourceType = resource.submitResourceType;
  if (!resourceId || !resourceType || !resource.resourceUrl) return null;
  rememberReferenceSourceHint(resource);
  return {
    resourceType,
    resourceId,
    role:
      resource.generationReferenceRole ??
      roleFromMedia(resource.mimeType, resourceType, resource.type),
    ordinal,
    snapshot: {
      // 命名统一：画布连线/共享引用与编辑页同源，使用 buildMentionDisplay 去掉 @ 后的 `类型[名V{seq}]`，
      // 保证连线引用名、编辑页 @ 名、历史匹配名三者一致。
      name:
        buildMentionDisplay(resource).replace(/^@/, '').trim() ||
        resource.tagLabel ||
        resource.elementName,
      url: resource.resourceUrl,
      thumbnailUrl: resource.thumbnailUrl,
      mimeType: normalizeMentionSnapshotMime(resource),
      width: resource.width,
      height: resource.height,
      durationSeconds: resource.durationSeconds,
    },
  };
}

/** 应用提示词还原的上传参考板 → 草稿线条目。 */
export function buildGenerationReferenceFromReferenceImage(
  image: ReferenceImage,
  ordinal = 0
): GenerationReferenceItem | null {
  const resourceType = resourceTypeFromReference(image);
  const resourceId = image.resourceId;
  const url = image.remoteUrl ?? image.url;
  if (!resourceType || !resourceId || !url) return null;
  return {
    resourceType,
    resourceId,
    role: roleFromMedia(image.mimeType, resourceType),
    ordinal,
    snapshot: {
      name: (image.name ?? '').replace(/^@/, '').trim() || '参考图',
      url,
      thumbnailUrl: image.url || undefined,
      mimeType: image.mimeType,
      width: image.width ?? undefined,
      height: image.height ?? undefined,
      durationSeconds: image.durationSeconds,
    },
  };
}

export function buildComposerGenerationReferences(params: {
  mentionTokens: MentionToken[];
  referenceImages: ReferenceImage[];
  firstFrame?: FrameImage | null;
  lastFrame?: FrameImage | null;
  videoMode?: VideoGenerationMode;
}): GenerationReferenceItem[] {
  const references = params.mentionTokens
    .map((token) => buildReferenceFromToken(token, params.referenceImages))
    .filter((item): item is GenerationReferenceItem => item !== null);
  if (params.firstFrame && params.videoMode !== 'video_extend') {
    const first = buildReferenceFromFrame(params.firstFrame, GenerationReferenceRole.FIRST_FRAME);
    if (first) references.push(first);
  }
  if (params.lastFrame) {
    const last = buildReferenceFromFrame(params.lastFrame, GenerationReferenceRole.LAST_FRAME);
    if (last) references.push(last);
  }
  return normalizeGenerationReferences(references);
}

const SNAPSHOT_NAME_TYPE_PREFIX: Record<string, MentionResourceType> = {
  角色: 'character',
  场景: 'location',
  道具: 'prop',
  实拍: 'footage',
  文件: 'footage',
  分镜: 'storyboard',
  视频: 'video',
  音效: 'sound',
  配音: 'voice',
  参考图: 'footage',
  参考视频: 'video',
  参考音频: 'sound',
};

const SNAPSHOT_NAME_TYPE_PREFIX_PATTERN =
  '角色|场景|道具|实拍|文件|分镜|视频|音效|配音|参考图|参考视频|参考音频';

/** 从 snapshot.name（可能已是 `分镜[名]` / `文件[名]`）解析 elementName，避免二次包一层。 */
function elementNameFromSnapshotName(name: string | undefined): string {
  let value = name?.trim() ?? '';
  // 循环剥离：兼容历史脏数据 `文件[文件[名]]`
  for (let i = 0; i < 3; i += 1) {
    const match = value.match(new RegExp(`^@?(${SNAPSHOT_NAME_TYPE_PREFIX_PATTERN})\\[(.+)\\]$`));
    if (!match?.[2]) break;
    value = match[2].trim();
  }
  return value;
}

/**
 * 从展示名中拆出版本：`名（V2）` / `名V2` → { baseName, versionSeq }。
 * 用于 draft 回灌 MentionResource 时恢复列表用的 versionSeq，避免列表变成无括号 `名V2`。
 */
export function splitMentionDisplayNameAndVersion(displayName: string): {
  baseName: string;
  versionSeq: number | null;
} {
  const value = displayName.trim();
  const fullWidth = value.match(/^(.*)（V(\d+)）$/);
  if (fullWidth) {
    return {
      baseName: fullWidth[1]!.trim(),
      versionSeq: Number(fullWidth[2]),
    };
  }
  const bare = value.match(/^(.*)V(\d+)$/);
  if (bare) {
    return {
      baseName: bare[1]!.trim(),
      versionSeq: Number(bare[2]),
    };
  }
  return { baseName: value, versionSeq: null };
}

/** 从 snapshot.name 前缀恢复 mention 类型（PROJECT_RESOURCE=1 无子类型时必需）。 */
function mentionResourceTypeFromSnapshotName(name: string | undefined): MentionResourceType | null {
  const value = name?.trim() ?? '';
  const match = value.match(new RegExp(`^@?(${SNAPSHOT_NAME_TYPE_PREFIX_PATTERN})\\[`));
  if (!match?.[1]) return null;
  return SNAPSHOT_NAME_TYPE_PREFIX[match[1]] ?? null;
}

function mentionResourceType(reference: GenerationReferenceItem): MentionResourceType {
  // resourceType 优先生类型，避免分镜图（2）被 mime/role 兜底成 footage→实拍。
  switch (reference.resourceType) {
    case GenerationResourceType.STORYBOARD_IMAGE:
      return 'storyboard';
    case GenerationResourceType.STORYBOARD_VIDEO:
      return 'video';
    case GenerationResourceType.SOUND_EFFECT:
      return 'sound';
    case GenerationResourceType.VOICE_OVER:
      return 'voice';
    case GenerationResourceType.HUMAN_ASSET:
      return 'character';
    default:
      break;
  }
  // 项目资产（1）本身不区分角色/场景/道具；连线回灌时从 snapshot.name 前缀恢复，避免一律变成「实拍」。
  const fromName = mentionResourceTypeFromSnapshotName(reference.snapshot.name);
  if (fromName) return fromName;
  if (
    reference.role === GenerationReferenceRole.REFERENCE_VIDEO ||
    reference.role === GenerationReferenceRole.VIDEO_EXTEND_SOURCE ||
    reference.snapshot.mimeType?.startsWith('video/')
  ) {
    return 'video';
  }
  if (
    reference.role === GenerationReferenceRole.REFERENCE_AUDIO ||
    reference.snapshot.mimeType?.startsWith('audio/')
  ) {
    // switch 已覆盖 SOUND_EFFECT / VOICE_OVER；此处仅剩项目资产/附件的音频 mime 兜底。
    return 'sound';
  }
  return 'footage';
}

/** snapshot.name 是否为「上传参考」类展示名（参考图/参考视频/参考音频）。 */
export function isUploadReferenceSnapshotName(name: string | undefined): boolean {
  return /^@?(参考图|参考视频|参考音频)\[/.test(name?.trim() ?? '');
}

export function buildGenerationReferenceMentionResources(
  references: GenerationReferenceItem[]
): MentionResource[] {
  return references.map((reference) => {
    const rawElementName = elementNameFromSnapshotName(reference.snapshot.name);
    const { baseName, versionSeq } = splitMentionDisplayNameAndVersion(rawElementName);
    const type = mentionResourceType(reference);
    const rawMime = reference.snapshot.mimeType ?? undefined;
    // 回灌时封面 mime 不得压过 reference_video / type=video。
    const mimeType =
      type === 'video' && rawMime && !rawMime.toLowerCase().startsWith('video/')
        ? 'video/mp4'
        : type === 'sound' && rawMime && !rawMime.toLowerCase().startsWith('audio/')
          ? 'audio/mpeg'
          : rawMime;
    // 角色/场景/道具列表名是 `elementName-tagLabel`；snapshot 里已是拼好的展示名，拆不出 tag 时整段作 elementName。
    return {
      type,
      elementId: reference.resourceId,
      elementName: baseName,
      resourceId: reference.resourceId,
      resourceUrl: reference.snapshot.url,
      thumbnailUrl: reference.snapshot.thumbnailUrl ?? undefined,
      mimeType,
      submitResourceId: reference.resourceId,
      submitResourceType: reference.resourceType,
      generationReferenceRole: reference.role,
      versionSeq,
      width: reference.snapshot.width ?? undefined,
      height: reference.snapshot.height ?? undefined,
      durationSeconds: reference.snapshot.durationSeconds ?? undefined,
      groupLabel: '共享引用',
      // 连线入框会重建 MentionResource；必须保留上传参考展示，否则 @参考图 会退化成 @分镜/@角色。
      forceUploadReferenceTab:
        reference.resourceType === GenerationResourceType.ATTACHMENT ||
        isUploadReferenceSnapshotName(reference.snapshot.name),
    };
  });
}

export function buildGenerationReferenceMentionTokens(
  text: string,
  references: GenerationReferenceItem[]
): MentionToken[] {
  const resources = buildGenerationReferenceMentionResources(references);
  const occupiedRanges: Array<{ start: number; end: number }> = [];
  const tokens: MentionToken[] = [];

  references.forEach((reference, index) => {
    const resource = resources[index];
    if (!resource) return;
    const display = `@${reference.snapshot.name.trim()}`;
    if (display.length <= 1) return;
    let searchFrom = 0;
    while (searchFrom < text.length) {
      const start = text.indexOf(display, searchFrom);
      if (start < 0) return;
      const end = start + display.length;
      searchFrom = end;
      if (occupiedRanges.some((range) => start < range.end && end > range.start)) {
        continue;
      }
      occupiedRanges.push({ start, end });
      tokens.push({
        id: `generation-reference:${buildGenerationReferenceIdentity(reference)}:${start}:${end}`,
        start,
        end,
        display,
        kind: 'resource',
        resource,
      });
      return;
    }
  });

  return tokens.sort((left, right) => left.start - right.start);
}

export function buildLocalGenerationReferenceTokens(params: {
  mentionTokens: MentionToken[];
  referenceImages: ReferenceImage[];
}): LocalGenerationReferenceToken[] {
  return params.mentionTokens.flatMap((token) => {
    const reference = buildReferenceFromToken(token, params.referenceImages);
    if (reference) {
      return [
        {
          identity: buildGenerationReferenceIdentity(reference),
          display: token.display,
          start: token.start,
          end: token.end,
        },
      ];
    }
    // 水合出来的 MentionNode 可能暂时没有 resourceUrl（服务端草稿尚未补齐），
    // 仍须持久化 identity，否则跨页 debounce 写回会丢掉 chip，下次水合变成纯文本。
    if (token.kind === 'resource' && token.resource) {
      const resource = token.resource;
      const resourceId = resource.submitResourceId ?? resource.resourceId;
      const resourceType = resource.submitResourceType;
      if (!resourceId || resourceType == null) return [];
      const role =
        resource.generationReferenceRole ??
        roleFromMedia(resource.mimeType, resourceType, resource.type);
      return [
        {
          identity: buildGenerationReferenceIdentity({ resourceType, resourceId, role }),
          display: token.display,
          start: token.start,
          end: token.end,
        },
      ];
    }
    return [];
  });
}

/**
 * 把 display 定位到一个未被占用的文本区间：记录的 start/end 仍对得上且未被占用时原样沿用，
 * 否则从头取第一个未占用的出现位置；找不到返回 null（display 已被删或出现次数不够）。
 *
 * 同一素材被重复插成多个 chip 时，靠调用方逐条 push 到 occupiedRanges 让它们各自错开。
 */
function resolveFreeDisplayRange(params: {
  text: string;
  display: string;
  start: number;
  end: number;
  occupiedRanges: ReadonlyArray<{ start: number; end: number }>;
}): { start: number; end: number } | null {
  if (!params.display) return null;
  const isOccupied = (start: number, end: number) =>
    params.occupiedRanges.some((range) => start < range.end && end > range.start);

  const recordedValid =
    params.start >= 0 &&
    params.end <= params.text.length &&
    params.start < params.end &&
    params.text.slice(params.start, params.end) === params.display;
  if (recordedValid && !isOccupied(params.start, params.end)) {
    return { start: params.start, end: params.end };
  }

  let searchFrom = 0;
  while (searchFrom <= params.text.length) {
    const start = params.text.indexOf(params.display, searchFrom);
    if (start < 0) return null;
    const end = start + params.display.length;
    if (!isOccupied(start, end)) return { start, end };
    searchFrom = end;
  }
  return null;
}

/**
 * 写出 localStorage 前合并：文本里仍出现的旧 token display 不得被「无 URL 的瞬时 mentionTokens」冲掉。
 * 用户真删 chip 后 display 不在文本中，不会被复活。
 *
 * 同一 identity 允许多条（用户可把同一素材重复插成多个 chip），各自占一个文本区间。
 * previous 的回填只针对「next 一条都没放下」的 identity：next 已放下至少一条时，
 * 说明该 identity 当前状态是准的，再拿 previous 补会把正文里用户手打的同名文本收编成 chip。
 */
export function mergeLocalGenerationReferenceTokensForPersist(params: {
  text: string;
  nextTokens: LocalGenerationReferenceToken[];
  previousTokens?: LocalGenerationReferenceToken[] | null;
}): LocalGenerationReferenceToken[] {
  const merged: LocalGenerationReferenceToken[] = [];
  const occupiedRanges: Array<{ start: number; end: number }> = [];

  const tryPlace = (token: LocalGenerationReferenceToken): void => {
    const range = resolveFreeDisplayRange({
      text: params.text,
      display: token.display,
      start: token.start,
      end: token.end,
      occupiedRanges,
    });
    if (!range) return;
    occupiedRanges.push(range);
    merged.push({ ...token, ...range });
  };

  // 先放 next（当前位置更准），再补 previous 中仍在文本、却被 next 整组漏掉的。
  for (const token of params.nextTokens) {
    tryPlace(token);
  }
  // 两道守卫都取「next 阶段结束时」的快照，不能改成随 merged 增长的实时集合：
  // previous 回填同 identity 的多条 token 是重复 chip 的恢复路径，实时判断会把第二条起全挡掉。
  const placedIdentities = new Set(merged.map((token) => token.identity));
  const placedDisplays = new Set(merged.map((token) => token.display));
  for (const token of params.previousTokens ?? []) {
    if (placedIdentities.has(token.identity)) continue;
    if (placedDisplays.has(token.display)) continue;
    tryPlace(token);
  }

  return merged.sort((left, right) => left.start - right.start);
}

function mentionResourceTypeFromResourceType(
  resourceType: GenerationResourceTypeValue
): MentionResourceType {
  switch (resourceType) {
    case GenerationResourceType.STORYBOARD_IMAGE:
      return 'storyboard';
    case GenerationResourceType.STORYBOARD_VIDEO:
      return 'video';
    case GenerationResourceType.SOUND_EFFECT:
      return 'sound';
    case GenerationResourceType.VOICE_OVER:
      return 'voice';
    case GenerationResourceType.HUMAN_ASSET:
      return 'character';
    default:
      return 'footage';
  }
}

/**
 * 草稿水合用引用集合：服务端 generation draft ∪ 本地快照（按 identity 去重，server 项优先）。
 *
 * 为什么必须合并而不是「server 非空就丢 local」：chip 已入框、引用 PATCH 尚未落地（或失败）
 * 时切换选中节点 / 刷新，server 草稿里还没有该 identity，只看 server 会让 chip 退化成无
 * `resourceUrl` 的空壳（提交时按 URL 过滤被静默丢弃），只能从本地快照拿回 `snapshot.url`。
 * 合并结果只用于回填 chip 的展示与 URL，不会写回草稿连线，所以没有「替用户连线」的反向风险。
 * 只增不减：server 项的顺序与 ordinal 保持原样，local 补项在尾部续排 ordinal。
 */
export function resolveDraftHydrationGenerationReferences(params: {
  serverReferences?: readonly GenerationReferenceItem[] | null;
  localReferences?: readonly GenerationReferenceItem[] | null;
}): GenerationReferenceItem[] {
  const serverReferences = params.serverReferences ?? [];
  const localReferences = params.localReferences ?? [];
  if (serverReferences.length === 0) {
    return localReferences.length > 0 ? normalizeGenerationReferences(localReferences) : [];
  }

  const seen = new Set(serverReferences.map(buildGenerationReferenceIdentity));
  const extras: GenerationReferenceItem[] = [];
  for (const reference of localReferences) {
    const identity = buildGenerationReferenceIdentity(reference);
    if (seen.has(identity)) continue;
    seen.add(identity);
    extras.push(reference);
  }
  if (extras.length === 0) return [...serverReferences];

  let nextOrdinal =
    serverReferences.reduce((max, reference) => Math.max(max, reference.ordinal), -1) + 1;
  return [
    ...serverReferences,
    ...extras.map((reference) => ({
      ...reference,
      ordinal: nextOrdinal++,
      snapshot: { ...reference.snapshot },
    })),
  ];
}

/**
 * 草稿水合：用本地 generationReferenceTokens（含画布自动入框的角色等）重建 MentionToken。
 * 优先用服务端 generationReferenceDraft.references 补全 URL；找不到时仍按 identity+display
 * 生成最小 resource，保证 Lexical 恢复为 MentionNode 而非纯文本/EmotionTag。
 *
 * 同 display 的多条 token（同一素材被重复插入）各自归位：记录的 start/end 失效时逐个往后找
 * 下一个未占用的出现位置，否则文本被编辑过后只能还原一条，其余会退化成纯文本。
 */
export function buildMentionTokensFromLocalGenerationReferenceTokens(params: {
  text: string;
  localTokens: LocalGenerationReferenceToken[];
  references: GenerationReferenceItem[];
  occupiedRanges?: Array<{ start: number; end: number }>;
}): MentionToken[] {
  const occupiedRanges = [...(params.occupiedRanges ?? [])];

  const referenceByIdentity = new Map(
    params.references.map((reference) => [buildGenerationReferenceIdentity(reference), reference])
  );
  const referenceByResourceId = new Map(
    params.references.map((reference) => [reference.resourceId, reference])
  );

  const tokens: MentionToken[] = [];

  for (const local of params.localTokens) {
    const range = resolveFreeDisplayRange({
      text: params.text,
      display: local.display,
      start: local.start,
      end: local.end,
      occupiedRanges,
    });
    if (!range) continue;
    const { start, end } = range;

    const parsed = parseGenerationReferenceIdentity(local.identity);
    const reference =
      referenceByIdentity.get(local.identity) ??
      (parsed ? referenceByResourceId.get(parsed.resourceId) : undefined);
    let resource: MentionResource | undefined;

    if (reference) {
      resource = buildGenerationReferenceMentionResources([reference])[0];
    } else {
      if (!parsed) continue;
      const snapshotName = local.display.replace(/^@/, '').trim();
      const type =
        mentionResourceTypeFromSnapshotName(snapshotName) ??
        mentionResourceTypeFromResourceType(parsed.resourceType);
      const rawElementName = elementNameFromSnapshotName(snapshotName);
      const { baseName, versionSeq } = splitMentionDisplayNameAndVersion(rawElementName);
      resource = {
        type,
        elementId: parsed.resourceId,
        elementName: baseName,
        resourceId: parsed.resourceId,
        submitResourceId: parsed.resourceId,
        submitResourceType: parsed.resourceType,
        generationReferenceRole: parsed.role,
        versionSeq,
        forceUploadReferenceTab: isUploadReferenceSnapshotName(snapshotName),
      };
    }

    if (!resource) continue;

    occupiedRanges.push({ start, end });
    tokens.push({
      id: `generation-reference:${local.identity}:${start}:${end}`,
      start,
      end,
      display: local.display,
      kind: 'resource',
      resource,
    });
  }

  return tokens.sort((left, right) => left.start - right.start);
}
